/// VariableNode: A single variable entry in the variable tree.
/// Displays the variable name, type-colored value (for primitives), or
/// an expandable section for objects and arrays.

import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { VariableTree } from "./VariableTree";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VariableNodeProps {
  name: string;
  value: unknown;
  path: string;
}

// ---------------------------------------------------------------------------
// Value type helpers
// ---------------------------------------------------------------------------

function getValueType(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return "string";
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  if (Array.isArray(value)) return "array";
  return "object";
}

function getValueColor(type: string): string {
  switch (type) {
    case "string":
      return "text-green-400";
    case "number":
      return "text-blue-400";
    case "boolean":
      return "text-red-400";
    case "null":
    case "undefined":
      return "text-zinc-500 italic";
    default:
      return "text-zinc-300";
  }
}

function formatValue(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "string") return `"${value}"`;
  return String(value);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VariableNode({ name, value, path }: VariableNodeProps) {
  const [expanded, setExpanded] = useState(false);
  const isExpandable = typeof value === "object" && value !== null;
  const valueType = getValueType(value);

  return (
    <div>
      <div
        className={`flex items-center gap-1 px-2 py-0.5 hover:bg-zinc-800 text-xs ${
          isExpandable ? "cursor-pointer" : "cursor-default"
        }`}
        onClick={() => isExpandable && setExpanded(!expanded)}
      >
        {/* Expand/collapse arrow */}
        {isExpandable ? (
          <ChevronRight
            size={12}
            className={`text-zinc-500 transition-transform shrink-0 ${expanded ? "rotate-90" : ""}`}
          />
        ) : (
          <span className="w-3 shrink-0" />
        )}

        {/* Variable name */}
        <span className="text-purple-400 font-medium shrink-0">{name}</span>
        <span className="text-zinc-600 shrink-0">:</span>

        {/* Value (inline for primitives, type badge for objects) */}
        {!isExpandable ? (
          <span className={`ml-1 truncate ${getValueColor(valueType)}`}>{formatValue(value)}</span>
        ) : (
          <span className="ml-1 text-zinc-500 text-[10px]">
            {Array.isArray(value)
              ? `Array(${value.length})`
              : `Object{${Object.keys(value as object).length}}`}
          </span>
        )}
      </div>

      {/* Nested children */}
      {expanded && isExpandable && (
        <div className="ml-3 border-l border-zinc-700">
          {Array.isArray(value) ? (
            value.map((item, idx) => (
              <VariableNode key={idx} name={`[${idx}]`} value={item} path={`${path}[${idx}]`} />
            ))
          ) : (
            <VariableTree data={value as Record<string, unknown>} path={path} />
          )}
        </div>
      )}
    </div>
  );
}
